import React, { useState, useEffect, useCallback, useRef } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Plus, Settings, Grid, List, Edit2, Trash2, Copy, Layers, Save, ChevronRight, GitBranch, ArrowRight, Clock, GripVertical, Users, Search, ChevronDown, Palette, ArrowUp, ArrowDown, ArrowUpDown, FileText, FileSpreadsheet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestaoOperacoes() {
  const [operacoes, setOperacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showModeloDialog, setShowModeloDialog] = useState(false);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [editingOperacao, setEditingOperacao] = useState(null);
  const [editingModelo, setEditingModelo] = useState(null);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todos');
  const [viewMode, setViewMode] = useState('grid'); // 'categories', 'grid', 'list', 'flow'
  const [activeTab, setActiveTab] = useState('operacoes'); // 'operacoes', 'modelos' ou 'categorias'
  const [draggedOp, setDraggedOp] = useState(null);
  const [dragOverOp, setDragOverOp] = useState(null);
  const [draggedCategoria, setDraggedCategoria] = useState(null);
  const [dragOverCategoria, setDragOverCategoria] = useState(null);
  const [permissoesOperadores, setPermissoesOperadores] = useState({}); // {operador_id: [{categoria_id, operacao_id}]}
  const [expandedModeloCategories, setExpandedModeloCategories] = useState([]); // Categorias expandidas no modal de modelo
  const [expandedCategories, setExpandedCategories] = useState([]); // Categorias expandidas na vista de categorias
  const [insertPosition, setInsertPosition] = useState(null); // { categoriaId, ordem, position: 'above' | 'below' }
  // Ordenação da lista/tabela: campo = 'codigo' | 'nome' | 'categoria' | 'tempo' | null (ordem original)
  const [ordenacao, setOrdenacao] = useState({ campo: null, dir: 'asc' });
  const ordenacaoTempo = ordenacao.campo === 'tempo' ? ordenacao.dir : null;
  const scrollContainerRef = useRef(null);
  const autoScrollRef = useRef(null);
  
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    tempo_standard: '',
    categoria: '',
    apenas_tarefas: false,
    is_contagem: false,
    tipo_contagem: 'fim',  // 'unitaria' = conta peça a peça | 'fim' = conta só no final (padrão)
    eficiencia_vermelho_min: 0,
    eficiencia_vermelho_max: 75,
    eficiencia_amarelo_min: 76,
    eficiencia_amarelo_max: 89,
    eficiencia_verde_min: 90,
    eficiencia_verde_max: 100,
    eficiencia_azul_min: 101
  });

  const [modeloForm, setModeloForm] = useState({
    nome: '',
    descricao: '',
    operacoes: []
  });

  const [categoriaForm, setCategoriaForm] = useState({
    nome: '',
    codigo: '',
    cor: '#3b82f6',
    operacoes_ids: [],
    destaque_app: false,
    especial: false
  });

  const [searchOperacao, setSearchOperacao] = useState(''); // Pesquisa de operações no diálogo
  const [searchTermo, setSearchTermo] = useState(''); // Pesquisa global de operações

  const CORES_CATEGORIAS = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#f97316', // orange
    '#eab308', // yellow
    '#22c55e', // green
    '#06b6d4', // cyan
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#6b7280', // gray
    '#78350f', // brown
  ];

  useEffect(() => {
    loadCategorias();
    loadModelos();
    loadOperadores();
    
    // Verificar se tem parâmetro new=1 para abrir modal de criação
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('new') === '1') {
      setActiveTab('operacoes');
      setShowDialog(true);
      setEditingOperacao(null);
      setFormData({
        codigo: '',
        nome: '',
        tempo_standard: '',
        categoria: '',
        apenas_tarefas: false,
        is_contagem: false,
        eficiencia_vermelho_min: 0,
        eficiencia_vermelho_max: 75,
        eficiencia_amarelo_min: 76,
        eficiencia_amarelo_max: 89,
        eficiencia_verde_min: 90,
        eficiencia_verde_max: 100,
        eficiencia_azul_min: 101
      });
      // Limpar o parâmetro da URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Carregar operações após categorias estarem disponíveis
  useEffect(() => {
    if (categorias.length > 0) {
      loadOperacoes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorias]);

  const loadOperacoes = async () => {
    try {
      const response = await axios.get(`${API}/operacoes`);
      // Normalizar dados: garantir que campos usados no frontend existem
      const opsNormalizadas = response.data.map(op => ({
        ...op,
        categoria: op.categoria || op.categoria_nome || '',
        tempo_standard: op.tempo_standard || op.tempo_padrao_segundos || 0
      }));
      
      // Ordenar operações: primeiro pela ordem da categoria, depois pela ordem dentro da categoria
      const opsOrdenadas = opsNormalizadas.sort((a, b) => {
        // Encontrar a ordem da categoria de cada operação
        const catA = categorias.find(c => c.nome === a.categoria);
        const catB = categorias.find(c => c.nome === b.categoria);
        const ordemCatA = catA?.ordem ?? 999;
        const ordemCatB = catB?.ordem ?? 999;
        
        // Primeiro ordenar por categoria
        if (ordemCatA !== ordemCatB) {
          return ordemCatA - ordemCatB;
        }
        
        // Dentro da mesma categoria, ordenar pela ordem da operação
        return (a.ordem || 0) - (b.ordem || 0);
      });
      setOperacoes(opsOrdenadas);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await axios.get(`${API}/categorias`);
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadModelos = async () => {
    try {
      const response = await axios.get(`${API}/modelos`);
      setModelos(response.data);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  };

  const loadOperadores = async () => {
    try {
      const response = await axios.get(`${API}/operadores`);
      const operadoresFiltrados = response.data.filter(o => o.perfil === 'operador');
      setOperadores(operadoresFiltrados);
      
      // Carregar permissões de cada operador
      const permissoes = {};
      for (const op of operadoresFiltrados) {
        try {
          const permRes = await axios.get(`${API}/operadores/${op.id}/permissoes`);
          const data = permRes.data;
          
          // Transformar formato {permissoes_categorias: {cat_id: [op_ids]}} para [{operacao_id, categoria_id}]
          if (data && data.permissoes_categorias) {
            const permList = [];
            for (const [catId, opIds] of Object.entries(data.permissoes_categorias)) {
              for (const opId of opIds) {
                permList.push({ operacao_id: opId, categoria_id: catId });
              }
            }
            permissoes[op.id] = permList;
          } else if (Array.isArray(data)) {
            permissoes[op.id] = data;
          } else {
            permissoes[op.id] = [];
          }
        } catch {
          permissoes[op.id] = [];
        }
      }
      setPermissoesOperadores(permissoes);
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
    }
  };

  // Verifica se um operador tem permissão para uma operação numa categoria específica
  const operadorTemPermissao = (operadorId, operacaoId, categoriaId = null) => {
    const permissoes = permissoesOperadores[operadorId];
    if (!permissoes || !Array.isArray(permissoes) || permissoes.length === 0) return false;
    
    if (categoriaId) {
      return permissoes.some(p => p.operacao_id === operacaoId && p.categoria_id === categoriaId);
    }
    return permissoes.some(p => p.operacao_id === operacaoId);
  };

  // Verifica se existe pelo menos um operador com permissão para a operação
  const existeOperadorComPermissao = (operacaoId, categoriaId = null) => {
    return operadores.some(op => operadorTemPermissao(op.id, operacaoId, categoriaId));
  };

  // Filtra operadores para uma operação - só mostra quem tem permissão explícita
  const filtrarOperadoresPorPermissao = (operacaoId, categoriaId = null) => {
    return operadores.filter(op => operadorTemPermissao(op.id, operacaoId, categoriaId));
  };

  // Formatar nome do operador (primeiro e último nome)
  const formatarNomeOperador = (nomeCompleto) => {
    if (!nomeCompleto) return '?';
    const partes = nomeCompleto.trim().split(' ');
    if (partes.length === 1) return partes[0];
    return `${partes[0]} ${partes[partes.length - 1]}`;
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      nome: '',
      tempo_standard: '',
      categoria: '',
      is_contagem: false,
      eficiencia_vermelho_min: 0,
      eficiencia_vermelho_max: 75,
      eficiencia_amarelo_min: 76,
      eficiencia_amarelo_max: 89,
      eficiencia_verde_min: 90,
      eficiencia_verde_max: 100,
      eficiencia_azul_min: 101
    });
    setEditingOperacao(null);
  };

  const resetModeloForm = () => {
    setModeloForm({
      nome: '',
      descricao: '',
      operacoes: []
    });
    setEditingModelo(null);
  };

  const resetCategoriaForm = () => {
    setCategoriaForm({
      nome: '',
      codigo: '',
      cor: '#3b82f6',
      operacoes_ids: [],
      destaque_app: false,
      especial: false
    });
    setEditingCategoria(null);
    setSearchOperacao('');
  };

  const handleEditCategoria = (categoria) => {
    setCategoriaForm({
      nome: categoria.nome,
      codigo: categoria.codigo || '',
      cor: categoria.cor || '#3b82f6',
      operacoes_ids: categoria.operacoes_ids || [],
      destaque_app: categoria.destaque_app || false,
      especial: categoria.especial || false
    });
    setEditingCategoria(categoria);
    setShowCategoriaDialog(true);
  };

  const handleSubmitCategoria = async (e) => {
    e.preventDefault();
    if (!categoriaForm.nome.trim()) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }

    setLoading(true);
    try {
      if (editingCategoria) {
        await axios.put(`${API}/categorias/${editingCategoria.id}`, categoriaForm);
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/categorias`, categoriaForm);
        toast.success('Categoria criada!');
      }
      setShowCategoriaDialog(false);
      resetCategoriaForm();
      loadCategorias();
    } catch (error) {
      toast.error('Erro ao guardar categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategoria = async (id) => {
    // Verificar se há operações usando esta categoria
    const categoria = categorias.find(c => c.id === id);
    const operacoesComCategoria = operacoes.filter(op => op.categoria === categoria?.nome);
    
    if (operacoesComCategoria.length > 0) {
      toast.error(`Não é possível eliminar. ${operacoesComCategoria.length} operação(ões) usa(m) esta categoria.`);
      return;
    }
    
    if (!window.confirm('Eliminar esta categoria?')) return;
    
    try {
      await axios.delete(`${API}/categorias/${id}`);
      toast.success('Categoria eliminada!');
      loadCategorias();
    } catch (error) {
      toast.error('Erro ao eliminar categoria');
    }
  };

  const handleEdit = (operacao) => {
    setFormData({
      codigo: operacao.codigo,
      nome: operacao.nome,
      tempo_standard: operacao.tempo_standard || operacao.tempo_padrao_segundos || '',
      categoria: operacao.categoria || operacao.categoria_nome || '',
      apenas_tarefas: operacao.apenas_tarefas || false,
      is_contagem: operacao.is_contagem || false,
      tipo_contagem: operacao.tipo_contagem || 'fim',
      eficiencia_vermelho_min: operacao.eficiencia_vermelho_min || 0,
      eficiencia_vermelho_max: operacao.eficiencia_vermelho_max || 75,
      eficiencia_amarelo_min: operacao.eficiencia_amarelo_min || 76,
      eficiencia_amarelo_max: operacao.eficiencia_amarelo_max || 89,
      eficiencia_verde_min: operacao.eficiencia_verde_min || 90,
      eficiencia_verde_max: operacao.eficiencia_verde_max || 100,
      eficiencia_azul_min: operacao.eficiencia_azul_min || 101
    });
    setEditingOperacao(operacao);
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Se não é apenas_tarefas, categoria é obrigatória
    if (!formData.codigo || !formData.nome || !formData.tempo_standard || (!formData.categoria && !formData.apenas_tarefas)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Verificar se o código já existe (só se o código foi alterado)
    const codigoNormalizado = formData.codigo.trim().toUpperCase();
    const codigoOriginal = editingOperacao?.codigo?.trim().toUpperCase();
    if (codigoNormalizado !== codigoOriginal) {
      const codigoDuplicado = operacoes.find(op => 
        op.codigo?.toUpperCase() === codigoNormalizado && 
        (!editingOperacao || op.id !== editingOperacao.id)
      );
      
      if (codigoDuplicado) {
        toast.error(`O código "${formData.codigo}" já existe na operação "${codigoDuplicado.nome}"`, {
          style: { fontSize: '1.05rem', padding: '16px 20px' },
          duration: 5000,
        });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        tempo_standard: parseInt(formData.tempo_standard),
        is_contagem: formData.is_contagem === true,  // Garantir que é boolean
        tipo_contagem: formData.tipo_contagem || 'fim',
        apenas_tarefas: formData.apenas_tarefas === true, // Flag para operações só de tarefas
        categoria: formData.apenas_tarefas ? null : formData.categoria, // Sem categoria se apenas_tarefas
        // Preservar a ordem existente ao editar
        ordem: editingOperacao ? (editingOperacao.ordem || 0) : 0
      };
      
      console.log('[OPERACAO] Payload a enviar:', { ...payload, is_contagem: payload.is_contagem });

      if (editingOperacao) {
        await axios.put(`${API}/operacoes/${editingOperacao.id}`, payload);
        toast.success('Operação atualizada com sucesso!');
      } else {
        // Criar nova operação
        const response = await axios.post(`${API}/operacoes`, payload);
        const novaOperacao = response.data;
        
        // Se temos uma posição de inserção, reordenar
        if (insertPosition) {
          // Encontrar todas as operações da mesma categoria
          const catOps = operacoes
            .filter(op => op.categoria === formData.categoria)
            .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
          
          // Encontrar posição de referência
          const refIndex = catOps.findIndex(op => op.id === insertPosition.referenceOpId);
          
          if (refIndex !== -1) {
            const newIndex = insertPosition.position === 'above' ? refIndex : refIndex + 1;
            
            // Inserir na posição correta
            catOps.splice(newIndex, 0, novaOperacao);
            
            // Reordenar todas as operações
            const ordens = catOps.map((op, idx) => ({
              id: op.id,
              ordem: idx + 1
            }));
            
            await axios.post(`${API}/operacoes/reordenar`, ordens);
          }
        }
        
        toast.success('Operação criada com sucesso!');
      }

      setShowDialog(false);
      setInsertPosition(null);
      resetForm();
      await loadOperacoes();
    } catch (error) {
      toast.error('Erro ao guardar operação');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOperacao = async (operacao) => {
    if (!confirm(`Eliminar operação "${operacao.nome}"?`)) return;
    
    try {
      await axios.delete(`${API}/operacoes/${operacao.id}`);
      toast.success('Operação eliminada');
      loadOperacoes();
    } catch (error) {
      toast.error('Erro ao eliminar operação');
    }
  };

  // Função para inserir operação acima ou abaixo de outra
  const handleInsertOperacao = (categoriaId, referenceOp, position) => {
    // Encontrar o nome da categoria a partir do ID
    const cat = categorias.find(c => c.id === categoriaId);
    const categoriaNome = cat?.nome || referenceOp.categoria;
    
    // Definir a categoria e posição de inserção
    setInsertPosition({
      categoriaId: categoriaId,
      referenceOpId: referenceOp.id,
      referenceOrdem: referenceOp.ordem || 1,
      position: position // 'above' ou 'below'
    });
    
    // Pré-preencher o formulário com a categoria (usa o nome, não o ID)
    setFormData({
      codigo: '',
      nome: '',
      tempo_standard: '',
      categoria: categoriaNome,
      apenas_tarefas: false,
      eficiencia_vermelho_min: 0,
      eficiencia_vermelho_max: 75,
      eficiencia_amarelo_min: 76,
      eficiencia_amarelo_max: 89,
      eficiencia_verde_min: 90,
      eficiencia_verde_max: 100,
      eficiencia_azul_min: 101
    });
    setEditingOperacao(null);
    setShowDialog(true);
  };

  const handleEditModelo = (modelo) => {
    // Ao editar, precisamos mapear as operações do modelo para incluir dados completos
    const operacoesMapeadas = (modelo.operacoes || []).map(op => {
      const opInfo = operacoes.find(o => o.id === op.operacao_id);
      return {
        operacao_id: op.operacao_id,
        codigo: opInfo?.codigo || op.codigo || '',
        nome: opInfo?.nome || op.nome || '',
        tempo_ajustado: op.tempo_ajustado || opInfo?.tempo_standard || 0,
        tempo_standard: opInfo?.tempo_standard || op.tempo_standard || 0,
        grupo: op.grupo || null,
        operadores_atribuidos: op.operadores_atribuidos || []
      };
    });
    
    setModeloForm({
      nome: modelo.nome,
      descricao: modelo.descricao || '',
      operacoes: operacoesMapeadas
    });
    setEditingModelo(modelo);
    setShowModeloDialog(true);
  };

  const handleSubmitModelo = async (e) => {
    e.preventDefault();
    if (!modeloForm.nome || modeloForm.operacoes.length === 0) {
      toast.error('Preencha o nome e selecione pelo menos uma operação');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nome: modeloForm.nome,
        descricao: modeloForm.descricao,
        operacoes: modeloForm.operacoes.map((op, idx) => ({
          operacao_id: op.operacao_id || op.id,
          tempo_ajustado: op.tempo_ajustado || op.tempo_standard,
          ordem: idx,
          grupo: op.grupo || null,
          operadores_atribuidos: op.operadores_atribuidos || []
        }))
      };

      if (editingModelo) {
        await axios.put(`${API}/modelos/${editingModelo.id}`, payload);
        toast.success('Minuta atualizada!');
      } else {
        await axios.post(`${API}/modelos`, payload);
        toast.success('Minuta criada!');
      }

      setShowModeloDialog(false);
      resetModeloForm();
      loadModelos();
    } catch (error) {
      toast.error('Erro ao guardar modelo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModelo = async (modelo) => {
    if (!confirm(`Eliminar modelo "${modelo.nome}"?`)) return;
    
    try {
      await axios.delete(`${API}/modelos/${modelo.id}`);
      toast.success('Minuta eliminada');
      loadModelos();
    } catch (error) {
      toast.error('Erro ao eliminar modelo');
    }
  };

  const toggleOperacaoNoModelo = (operacao) => {
    const exists = modeloForm.operacoes.find(op => (op.operacao_id || op.id) === operacao.id);
    if (exists) {
      setModeloForm({
        ...modeloForm,
        operacoes: modeloForm.operacoes.filter(op => (op.operacao_id || op.id) !== operacao.id)
      });
    } else {
      // Adicionar operação e reordenar pela ordem da categoria e ordem da operação
      const novaOp = {
        operacao_id: operacao.id,
        codigo: operacao.codigo,
        nome: operacao.nome,
        tempo_ajustado: operacao.tempo_standard,
        tempo_standard: operacao.tempo_standard,
        categoria: operacao.categoria,
        ordem: operacao.ordem || 0,
        grupo: null,
        operadores_atribuidos: []
      };
      
      const novasOperacoes = [...modeloForm.operacoes, novaOp];
      
      // Ordenar pela ordem da categoria e depois pela ordem da operação
      novasOperacoes.sort((a, b) => {
        const catA = categorias.find(c => c.nome === a.categoria);
        const catB = categorias.find(c => c.nome === b.categoria);
        const ordemCatA = catA?.ordem ?? 999;
        const ordemCatB = catB?.ordem ?? 999;
        
        if (ordemCatA !== ordemCatB) {
          return ordemCatA - ordemCatB;
        }
        return (a.ordem || 0) - (b.ordem || 0);
      });
      
      setModeloForm({
        ...modeloForm,
        operacoes: novasOperacoes
      });
    }
  };

  // Drag and drop para reordenar operações no modelo
  const [draggedModeloOp, setDraggedModeloOp] = useState(null);
  const [dragOverModeloOp, setDragOverModeloOp] = useState(null);

  const handleDragStartModeloOp = (e, index) => {
    setDraggedModeloOp(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverModeloOp = (e, index) => {
    e.preventDefault();
    if (draggedModeloOp !== null && draggedModeloOp !== index) {
      setDragOverModeloOp(index);
    }
  };

  const handleDropModeloOp = (e, index) => {
    e.preventDefault();
    if (draggedModeloOp === null || draggedModeloOp === index) return;
    
    const newOps = [...modeloForm.operacoes];
    const [draggedItem] = newOps.splice(draggedModeloOp, 1);
    newOps.splice(index, 0, draggedItem);
    
    setModeloForm({ ...modeloForm, operacoes: newOps });
    setDraggedModeloOp(null);
    setDragOverModeloOp(null);
  };

  const handleDragEndModeloOp = () => {
    setDraggedModeloOp(null);
    setDragOverModeloOp(null);
  };

  const updateOperacaoGrupo = (opId, grupo) => {
    setModeloForm({
      ...modeloForm,
      operacoes: modeloForm.operacoes.map(op => 
        (op.operacao_id || op.id) === opId ? { ...op, grupo } : op
      )
    });
  };

  const toggleOperadorNoModelo = (opIndex, operadorId) => {
    const newOps = [...modeloForm.operacoes];
    const op = newOps[opIndex];
    const operadoresAttr = op.operadores_atribuidos || [];
    
    if (operadoresAttr.includes(operadorId)) {
      newOps[opIndex] = {
        ...op,
        operadores_atribuidos: operadoresAttr.filter(id => id !== operadorId)
      };
    } else {
      newOps[opIndex] = {
        ...op,
        operadores_atribuidos: [...operadoresAttr, operadorId]
      };
    }
    setModeloForm({ ...modeloForm, operacoes: newOps });
  };

  const operacoesFiltradas = (() => {
    let ops = categoriaFiltro === 'todos' 
      ? operacoes 
      : operacoes.filter(op => op.categoria === categoriaFiltro);
    
    // Aplicar pesquisa por texto
    if (searchTermo.trim()) {
      const termo = searchTermo.trim().toLowerCase();
      ops = ops.filter(op => 
        (op.codigo || '').toLowerCase().includes(termo) ||
        (op.nome || '').toLowerCase().includes(termo) ||
        (op.categoria || '').toLowerCase().includes(termo)
      );
    }

    // Aplicar ordenação (cabeçalhos da tabela ou botão do relógio)
    if (ordenacao.campo) {
      const txt = (v) => (v || '').toString();
      const porCodigo = (a, b) => txt(a.codigo).localeCompare(txt(b.codigo), 'pt', { numeric: true, sensitivity: 'base' });
      const cmp = {
        codigo: porCodigo,
        nome: (a, b) => txt(a.nome).localeCompare(txt(b.nome), 'pt', { sensitivity: 'base' }),
        categoria: (a, b) => txt(a.categoria).localeCompare(txt(b.categoria), 'pt', { sensitivity: 'base' }),
        tempo: (a, b) => (a.tempo_standard || 0) - (b.tempo_standard || 0),
      }[ordenacao.campo];
      const sinal = ordenacao.dir === 'desc' ? -1 : 1;
      // Desempate sempre por código (A→Z) para uma ordem estável e previsível
      ops = [...ops].sort((a, b) => (sinal * cmp(a, b)) || porCodigo(a, b));
    }
    
    return ops;
  })();

  // Exportar a lista de operações (respeita filtro de categoria, pesquisa e ordenação visíveis)
  const [exportando, setExportando] = useState(null); // 'csv' | 'pdf' | null
  const exportarOperacoes = async (formato) => {
    const lista = operacoesFiltradas;
    if (!lista.length) { toast.error('Não há operações para exportar com os filtros actuais'); return; }
    const partes = [];
    partes.push(categoriaFiltro === 'todos' ? 'Todas as categorias' : `Categoria: ${categoriaFiltro}`);
    if (searchTermo.trim()) partes.push(`Pesquisa: "${searchTermo.trim()}"`);
    if (ordenacao.campo) {
      const nomes = { codigo: 'Código', nome: 'Nome', categoria: 'Categoria', tempo: 'Tempo' };
      const dirTxt = ordenacao.campo === 'tempo'
        ? (ordenacao.dir === 'desc' ? 'maior → menor' : 'menor → maior')
        : (ordenacao.dir === 'desc' ? 'Z → A' : 'A → Z');
      partes.push(`Ordenado por ${nomes[ordenacao.campo]}: ${dirTxt}`);
    }
    setExportando(formato);
    try {
      const res = await axios.post(`${API}/operacoes/exportar`, {
        formato,
        ids: lista.map((o) => o.id),
        filtro_label: partes.join(' · '),
      }, { responseType: 'blob' });
      const tipo = formato === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: tipo }));
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `operacoes_${stamp}.${formato}`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(`${lista.length} operações exportadas (${formato.toUpperCase()})`);
    } catch (e) {
      toast.error('Erro ao exportar operações');
    } finally {
      setExportando(null);
    }
  };

  // Alternar ordenação por um campo: 1.º clique ordena, 2.º inverte, 3.º volta à ordem original.
  // Texto começa em A→Z; Tempo começa em maior→menor (como o botão do relógio já fazia).
  const toggleOrdenacao = (campo) => {
    const primeira = campo === 'tempo' ? 'desc' : 'asc';
    setOrdenacao((o) => {
      if (o.campo !== campo) return { campo, dir: primeira };
      if (o.dir === primeira) return { campo, dir: primeira === 'asc' ? 'desc' : 'asc' };
      return { campo: null, dir: 'asc' };
    });
  };
  const toggleOrdenacaoTempo = () => toggleOrdenacao('tempo');

  const CabecalhoOrdenavel = ({ campo, children, align = 'left' }) => {
    const ativo = ordenacao.campo === campo;
    const Icone = !ativo ? ArrowUpDown : ordenacao.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={`py-3 px-4 text-sm font-semibold text-${align}`} aria-sort={ativo ? (ordenacao.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          onClick={() => toggleOrdenacao(campo)}
          className={`inline-flex items-center gap-1 select-none transition-colors hover:text-navy ${ativo ? 'text-blue-700' : 'text-stone-600'}`}
          title={ativo ? 'Clique para inverter / repor a ordem original' : 'Ordenar por esta coluna'}
          data-testid={`sort-header-${campo}`}
        >
          {children}
          <Icone className={`h-3.5 w-3.5 ${ativo ? '' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  const formatTempo = (segundos) => {
    if (segundos >= 60) {
      const min = Math.floor(segundos / 60);
      const sec = segundos % 60;
      return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
    }
    return `${segundos}s`;
  };

  const getCategoriaColor = (categoria) => {
    const colors = {
      'intermedios': 'bg-blue-100 text-blue-700',
      'acabamentos': 'bg-amber-100 text-amber-700',
      'embalamento': 'bg-green-100 text-green-700',
      'controlo qualidade': 'bg-purple-100 text-purple-700'
    };
    return colors[categoria?.toLowerCase()] || 'bg-stone-100 text-stone-600';
  };

  // Drag & Drop para reordenar operações na vista de fluxo
  const handleDragStartOp = (e, op) => {
    setDraggedOp(op);
    e.dataTransfer.effectAllowed = 'move';
    
    // Iniciar auto-scroll
    const handleAutoScroll = (moveEvent) => {
      const scrollContainer = document.documentElement;
      const scrollThreshold = 100; // pixels do edge para iniciar scroll
      const scrollSpeed = 15;
      
      const mouseY = moveEvent.clientY;
      const windowHeight = window.innerHeight;
      
      if (mouseY < scrollThreshold) {
        // Scroll para cima
        window.scrollBy(0, -scrollSpeed);
      } else if (mouseY > windowHeight - scrollThreshold) {
        // Scroll para baixo
        window.scrollBy(0, scrollSpeed);
      }
    };
    
    autoScrollRef.current = setInterval(() => {
      // O scroll será controlado pelo mousemove
    }, 50);
    
    document.addEventListener('dragover', handleAutoScroll);
    e.target.addEventListener('dragend', () => {
      document.removeEventListener('dragover', handleAutoScroll);
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    }, { once: true });
  };

  const handleDragOverOp = (e, op) => {
    e.preventDefault();
    
    // Auto-scroll durante drag
    const scrollThreshold = 100;
    const scrollSpeed = 10;
    const mouseY = e.clientY;
    const windowHeight = window.innerHeight;
    
    if (mouseY < scrollThreshold) {
      window.scrollBy(0, -scrollSpeed);
    } else if (mouseY > windowHeight - scrollThreshold) {
      window.scrollBy(0, scrollSpeed);
    }
    
    if (draggedOp && draggedOp.id !== op.id && draggedOp.categoria === op.categoria) {
      setDragOverOp(op);
    }
  };

  const handleDropOp = async (e, targetOp) => {
    e.preventDefault();
    if (!draggedOp || draggedOp.id === targetOp.id || draggedOp.categoria !== targetOp.categoria) {
      setDraggedOp(null);
      setDragOverOp(null);
      return;
    }

    // Reordenar operações da mesma categoria
    const catOps = operacoes.filter(op => op.categoria === draggedOp.categoria);
    const otherOps = operacoes.filter(op => op.categoria !== draggedOp.categoria);
    
    const draggedIndex = catOps.findIndex(op => op.id === draggedOp.id);
    const targetIndex = catOps.findIndex(op => op.id === targetOp.id);
    
    // Remover e inserir na nova posição
    catOps.splice(draggedIndex, 1);
    catOps.splice(targetIndex, 0, draggedOp);
    
    // Atualizar ordem
    const ordens = catOps.map((op, idx) => ({
      id: op.id,
      ordem: idx + 1
    }));

    // Atualizar estado local
    const newOperacoes = [...otherOps, ...catOps.map((op, idx) => ({ ...op, ordem: idx + 1 }))];
    setOperacoes(newOperacoes);
    
    setDraggedOp(null);
    setDragOverOp(null);

    // Salvar no backend
    try {
      await axios.post(`${API}/operacoes/reordenar`, ordens);
      toast.success('Ordem das operações atualizada');
      loadOperacoes(); // Recarregar para garantir consistência
    } catch (error) {
      console.error('Erro ao reordenar:', error);
      toast.error('Erro ao salvar a nova ordem');
      loadOperacoes();
    }
  };

  // Drag and Drop para Categorias
  const handleDragStartCategoria = (e, categoria) => {
    setDraggedCategoria(categoria);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverCategoria = (e, categoria) => {
    e.preventDefault();
    if (draggedCategoria && draggedCategoria.id !== categoria.id) {
      setDragOverCategoria(categoria);
    }
  };

  const handleDropCategoria = async (e, targetCategoria) => {
    e.preventDefault();
    if (!draggedCategoria || draggedCategoria.id === targetCategoria.id) {
      setDraggedCategoria(null);
      setDragOverCategoria(null);
      return;
    }

    // Reordenar categorias
    const catsCopy = [...categorias];
    const draggedIndex = catsCopy.findIndex(c => c.id === draggedCategoria.id);
    const targetIndex = catsCopy.findIndex(c => c.id === targetCategoria.id);
    
    // Remover e inserir na nova posição
    catsCopy.splice(draggedIndex, 1);
    catsCopy.splice(targetIndex, 0, draggedCategoria);
    
    // Atualizar ordem
    const ordens = catsCopy.map((cat, idx) => ({
      id: cat.id,
      ordem: idx + 1
    }));

    // Atualizar estado local
    setCategorias(catsCopy.map((cat, idx) => ({ ...cat, ordem: idx + 1 })));
    
    setDraggedCategoria(null);
    setDragOverCategoria(null);

    // Salvar no backend
    try {
      await axios.post(`${API}/categorias/reordenar`, ordens);
      toast.success('Ordem das categorias atualizada');
      loadCategorias();
    } catch (error) {
      console.error('Erro ao reordenar categorias:', error);
      toast.error('Erro ao salvar a nova ordem');
      loadCategorias();
    }
  };

  const handleDragEndCategoria = () => {
    setDraggedCategoria(null);
    setDragOverCategoria(null);
  };

  const handleDragEndOp = () => {
    setDraggedOp(null);
    setDragOverOp(null);
  };

  return (
    <GestorLayout>
      <div className="px-8 pt-4 pb-8">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Operações</h1>
            <p className="text-stone-600">Gerir operações e minutas de produção</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className="flex border rounded-full overflow-hidden mr-4">
              <Button
                variant={activeTab === 'operacoes' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('operacoes')}
                className="rounded-none"
              >
                <Settings className="h-4 w-4 mr-1" />
                Operações ({operacoes.length})
              </Button>
              <Button
                variant={activeTab === 'categorias' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('categorias')}
                className="rounded-none"
              >
                <Palette className="h-4 w-4 mr-1" />
                Categorias ({categorias.length})
              </Button>
              <Button
                variant={activeTab === 'modelos' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('modelos')}
                className="rounded-none"
              >
                <Layers className="h-4 w-4 mr-1" />
                Minutas ({modelos.length})
              </Button>
            </div>

            {/* Toggle vista */}
            {activeTab === 'operacoes' && (
              <div className="flex border rounded-full overflow-hidden">
                <Button
                  variant={viewMode === 'categories' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('categories')}
                  className="rounded-none"
                  title="Vista por categorias"
                >
                  <Layers className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-none"
                  title="Vista em grelha"
                  data-testid="view-mode-grid-btn"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-none"
                  title="Vista em lista"
                  data-testid="view-mode-list-btn"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'flow' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('flow')}
                  className="rounded-none"
                  title="Vista fluxo de produção"
                  data-testid="view-mode-flow-btn"
                >
                  <GitBranch className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            
            {activeTab === 'operacoes' && (
              <div className="flex border rounded-full overflow-hidden" data-testid="export-operacoes-group">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => exportarOperacoes('csv')}
                  disabled={!!exportando}
                  className="rounded-none gap-1"
                  title="Exportar a lista visível para CSV (Excel)"
                  data-testid="export-operacoes-csv-btn"
                >
                  <FileSpreadsheet className="h-4 w-4" /> {exportando === 'csv' ? 'A exportar...' : 'CSV'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => exportarOperacoes('pdf')}
                  disabled={!!exportando}
                  className="rounded-none gap-1 border-l"
                  title="Exportar a lista visível para PDF"
                  data-testid="export-operacoes-pdf-btn"
                >
                  <FileText className="h-4 w-4" /> {exportando === 'pdf' ? 'A exportar...' : 'PDF'}
                </Button>
              </div>
            )}

            <Button
              onClick={() => activeTab === 'operacoes' ? setShowDialog(true) : setShowModeloDialog(true)}
              className="rounded-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              {activeTab === 'operacoes' ? 'Nova Operação' : 'Nova Minuta'}
            </Button>
          </div>
        </div>

        {/* Tab Operações */}
        {activeTab === 'operacoes' && (
          <>
            {/* Pesquisa + Filtro */}
            <div className="mb-6">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <input
                  type="text"
                  value={searchTermo}
                  onChange={(e) => setSearchTermo(e.target.value)}
                  placeholder="Pesquisar por codigo ou nome (ex: AC01, Contagem...)"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  data-testid="search-operacoes-input"
                />
                {searchTermo && (
                  <button
                    onClick={() => setSearchTermo('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    data-testid="clear-search-operacoes"
                  >
                    &times;
                  </button>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={categoriaFiltro === 'todos' ? 'default' : 'outline'}
                  onClick={() => setCategoriaFiltro('todos')}
                  className="rounded-full"
                >
                  Todas ({operacoes.length})
                </Button>
                {categorias.map((cat) => {
                  const count = operacoes.filter(op => op.categoria === cat.nome).length;
                  return (
                    <Button
                      key={cat.id}
                      variant={categoriaFiltro === cat.nome ? 'default' : 'outline'}
                      onClick={() => setCategoriaFiltro(cat.nome)}
                      className="rounded-full capitalize"
                    >
                      {cat.nome} ({count})
                    </Button>
                  );
                })}
              </div>
              
              {/* Botão de Ordenação por Tempo */}
              <Button
                variant={ordenacaoTempo ? 'default' : 'outline'}
                onClick={toggleOrdenacaoTempo}
                className={`rounded-full gap-2 ${ordenacaoTempo ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                title={
                  ordenacaoTempo === 'desc' 
                    ? 'Ordenado: Maior → Menor (clique para inverter)' 
                    : ordenacaoTempo === 'asc' 
                      ? 'Ordenado: Menor → Maior (clique para desativar)' 
                      : 'Ordenar por tempo'
                }
              >
                <Clock className="h-4 w-4" />
                {ordenacaoTempo === 'desc' && <ArrowDown className="h-3 w-3" />}
                {ordenacaoTempo === 'asc' && <ArrowUp className="h-3 w-3" />}
                {!ordenacaoTempo && <ArrowUpDown className="h-3 w-3" />}
              </Button>
            </div>

            {/* Vista por Categorias (estilo selector de OF) */}
            {viewMode === 'categories' && (
              <div className="space-y-3">
                {categorias.map((categoria) => {
                  const catOps = operacoesFiltradas.filter(op => op.categoria === categoria.nome);
                  if (catOps.length === 0 && categoriaFiltro !== 'todos') return null;
                  
                  const isExpanded = expandedCategories.includes(categoria.id);
                  const catCor = categoria.cor || '#6b7280';
                  
                  return (
                    <div key={categoria.id} className="border rounded-lg overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={() => setExpandedCategories(prev => 
                          prev.includes(categoria.id) 
                            ? prev.filter(c => c !== categoria.id)
                            : [...prev, categoria.id]
                        )}
                        className="w-full flex items-center justify-between p-3 hover:bg-stone-50 text-sm font-medium text-stone-700 transition-colors"
                        style={{ backgroundColor: `${catCor}15`, borderLeft: `4px solid ${catCor}` }}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`h-5 w-5 transition-transform ${isExpanded ? '' : '-rotate-90'}`} style={{ color: catCor }} />
                          <span className="capitalize text-base font-semibold" style={{ color: catCor }}>{categoria.nome}</span>
                          <span className="text-sm text-stone-400 font-normal">({catOps.length} operações)</span>
                          {categoria.especial && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full font-medium">
                              Especial
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCategoria(categoria);
                            }}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="p-2 space-y-1 bg-white max-h-[400px] overflow-y-auto">
                          {catOps.length === 0 ? (
                            <p className="text-sm text-stone-400 text-center py-4">Nenhuma operação nesta categoria</p>
                          ) : (
                            catOps.map((operacao) => (
                              <div
                                key={operacao.id}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors group border border-transparent hover:border-stone-200"
                              >
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Settings className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{operacao.codigo}</span>
                                    <span className="font-medium text-stone-900 truncate">{operacao.nome}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1 text-stone-500">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span className="text-sm font-semibold">{formatTempo(operacao.tempo_standard)}</span>
                                  </div>
                                  <div className="flex items-center gap-0.5">
                                    <div className="w-2 h-2 rounded-full bg-red-500" title={`≤${operacao.eficiencia_vermelho_max || 75}%`}></div>
                                    <div className="w-2 h-2 rounded-full bg-yellow-500" title={`≤${operacao.eficiencia_amarelo_max || 89}%`}></div>
                                    <div className="w-2 h-2 rounded-full bg-green-500" title={`≤${operacao.eficiencia_verde_max || 100}%`}></div>
                                    <div className="w-2 h-2 rounded-full bg-blue-500" title={`>${operacao.eficiencia_azul_min || 101}%`}></div>
                                  </div>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(operacao)}>
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-error" onClick={() => handleDeleteOperacao(operacao)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                          {/* Botão para adicionar operação nesta categoria */}
                          <button
                            onClick={() => {
                              setFormData(prev => ({ ...prev, categoria: categoria.nome }));
                              setShowDialog(true);
                            }}
                            className="w-full flex items-center justify-center gap-2 p-2 mt-2 border-2 border-dashed border-stone-200 rounded-lg text-stone-400 hover:text-primary hover:border-primary transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                            <span className="text-sm">Adicionar operação a {categoria.nome}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Secção para Operações Apenas Tarefas */}
                {(() => {
                  const tarefasOps = operacoesFiltradas.filter(op => op.apenas_tarefas === true);
                  if (tarefasOps.length === 0 && categoriaFiltro !== 'todos') return null;
                  
                  const isExpanded = expandedCategories.includes('apenas-tarefas');
                  
                  return (
                    <div key="apenas-tarefas" className="border rounded-lg overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={() => setExpandedCategories(prev => 
                          prev.includes('apenas-tarefas') 
                            ? prev.filter(c => c !== 'apenas-tarefas')
                            : [...prev, 'apenas-tarefas']
                        )}
                        className="w-full flex items-center justify-between p-3 hover:bg-purple-50 text-sm font-medium text-stone-700 transition-colors"
                        style={{ backgroundColor: '#f3e8ff', borderLeft: '4px solid #9333ea' }}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`h-5 w-5 transition-transform text-purple-600 ${isExpanded ? '' : '-rotate-90'}`} />
                          <span className="text-base font-semibold text-purple-600">Apenas Tarefas</span>
                          <span className="text-sm text-stone-400 font-normal">({tarefasOps.length} operações)</span>
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full font-medium">
                            Tarefas Pontuais
                          </span>
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="p-2 space-y-1 bg-white max-h-[400px] overflow-y-auto">
                          {tarefasOps.length === 0 ? (
                            <p className="text-sm text-stone-400 text-center py-4">Nenhuma operação só para tarefas</p>
                          ) : (
                            tarefasOps.map((operacao) => (
                              <div
                                key={operacao.id}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 transition-colors group border border-transparent hover:border-purple-200"
                              >
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                  <Settings className="h-4 w-4 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">{operacao.codigo}</span>
                                    <span className="font-medium text-stone-900 truncate">{operacao.nome}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1 text-stone-500">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span className="text-sm font-semibold">{formatTempo(operacao.tempo_standard)}</span>
                                  </div>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(operacao)}>
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-error" onClick={() => handleDeleteOperacao(operacao)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                          {/* Botão para adicionar operação apenas tarefas */}
                          <button
                            onClick={() => {
                              setFormData(prev => ({ ...prev, categoria: '', apenas_tarefas: true }));
                              setShowDialog(true);
                            }}
                            className="w-full flex items-center justify-center gap-2 p-2 mt-2 border-2 border-dashed border-purple-200 rounded-lg text-purple-400 hover:text-purple-600 hover:border-purple-400 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                            <span className="text-sm">Adicionar operação para Tarefas Pontuais</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Vista Grid */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {operacoesFiltradas.map((operacao) => (
                  <Card key={operacao.id} className={`p-5 hover:shadow-lg transition-shadow group ${operacao.is_contagem ? 'ring-2 ring-sky-200' : ''}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${operacao.is_contagem ? 'bg-sky-100' : 'bg-primary/10'}`}>
                        {operacao.is_contagem ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        ) : (
                          <Settings className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-mono text-stone-500">{operacao.codigo}</p>
                          {operacao.is_contagem && (
                            <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium">Contagem</span>
                          )}
                          {operacao.tipo_contagem === 'unitaria' && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Unitária</span>
                          )}
                        </div>
                        <h3 className="text-sm font-heading font-bold text-stone-900 truncate">
                          {operacao.nome}
                        </h3>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(operacao)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-error" onClick={() => handleDeleteOperacao(operacao)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoriaColor(operacao.categoria)}`}>
                          {operacao.categoria}
                        </span>
                        <span className="text-sm font-bold text-stone-900">{formatTempo(operacao.tempo_standard)}</span>
                      </div>
                      
                      <div className="flex items-center gap-1 pt-2 border-t border-stone-100">
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-[10px] text-stone-400">{operacao.eficiencia_vermelho_max || 75}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          <span className="text-[10px] text-stone-400">{operacao.eficiencia_amarelo_max || 89}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-[10px] text-stone-400">{operacao.eficiencia_verde_max || 100}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          <span className="text-[10px] text-stone-400">&gt;{operacao.eficiencia_azul_min || 101}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Vista Lista */}
            {viewMode === 'list' && (
              <Card className="overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <CabecalhoOrdenavel campo="codigo">Código</CabecalhoOrdenavel>
                      <CabecalhoOrdenavel campo="nome">Nome</CabecalhoOrdenavel>
                      <CabecalhoOrdenavel campo="categoria">Categoria</CabecalhoOrdenavel>
                      <CabecalhoOrdenavel campo="tempo" align="center">Tempo</CabecalhoOrdenavel>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Eficiência</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operacoesFiltradas.map((operacao) => (
                      <tr key={operacao.id} className="border-t border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-4 font-mono font-bold text-primary">
                          <div className="flex items-center gap-2">
                            {operacao.codigo}
                            {operacao.is_contagem && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 rounded" title="Operação de Contagem">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </span>
                            )}
                            {operacao.tipo_contagem === 'unitaria' && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Unitária</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-semibold text-stone-900">{operacao.nome}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoriaColor(operacao.categoria)}`}>
                            {operacao.categoria}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center font-bold">{formatTempo(operacao.tempo_standard)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(operacao)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-error" onClick={() => handleDeleteOperacao(operacao)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {/* Vista Flow - Fluxo de Produção */}
            {viewMode === 'flow' && (
              <div className="space-y-4">
                {/* Explicação do fluxo */}
                <Card className="p-4 bg-gradient-to-r from-blue-50 to-primary/5 border-blue-200">
                  <div className="flex items-center gap-3">
                    <GitBranch className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-stone-800">Fluxo Típico de Produção</p>
                      <p className="text-sm text-stone-600">
                        Visualize a sequência de operações por categoria. <strong>Arraste para reordenar</strong> as operações dentro de cada categoria.
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Timeline horizontal de categorias */}
                <div className="flex items-center justify-center gap-2 py-4 flex-wrap">
                  {categorias.map((cat, idx) => (
                    <React.Fragment key={cat.id}>
                      <div className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all cursor-pointer hover:scale-105 ${
                        categoriaFiltro === cat.nome 
                          ? 'bg-primary text-white shadow-lg' 
                          : getCategoriaColor(cat.nome)
                      }`}
                        onClick={() => setCategoriaFiltro(cat.nome === categoriaFiltro ? 'todos' : cat.nome)}
                      >
                        <span className="font-bold mr-1">{idx + 1}.</span>
                        {cat.nome}
                        <span className="ml-2 opacity-70">
                          ({operacoes.filter(op => op.categoria === cat.nome).length})
                        </span>
                      </div>
                      {idx < categorias.length - 1 && (
                        <ArrowRight className="h-5 w-5 text-stone-300 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Operações por categoria em timeline vertical */}
                <div className="relative">
                  {categorias.map((cat, catIdx) => {
                    const catOps = operacoes.filter(op => op.categoria === cat.nome);
                    if (catOps.length === 0) return null;
                    
                    return (
                      <div key={cat.id} className="mb-8 last:mb-0">
                        {/* Cabeçalho da categoria */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            catIdx === 0 ? 'bg-blue-500' :
                            catIdx === 1 ? 'bg-amber-500' :
                            catIdx === 2 ? 'bg-green-500' :
                            catIdx === 3 ? 'bg-purple-500' :
                            'bg-stone-500'
                          }`}>
                            {catIdx + 1}
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-lg text-stone-900 capitalize">{cat.nome}</h3>
                            <p className="text-sm text-stone-500">
                              {catOps.length} operações • Tempo total: {formatTempo(catOps.reduce((acc, op) => acc + op.tempo_standard, 0))}
                            </p>
                          </div>
                        </div>

                        {/* Linha vertical */}
                        <div className="relative ml-5 border-l-2 border-stone-200 pl-8 pb-4">
                          {catOps.map((op, opIdx) => (
                            <div 
                              key={op.id} 
                              className={`relative mb-3 last:mb-0 group cursor-move ${
                                draggedOp?.id === op.id ? 'opacity-50 scale-95' : ''
                              } ${
                                dragOverOp?.id === op.id ? 'ring-2 ring-primary ring-offset-2 rounded-lg' : ''
                              }`}
                              draggable
                              onDragStart={(e) => handleDragStartOp(e, op)}
                              onDragOver={(e) => handleDragOverOp(e, op)}
                              onDrop={(e) => handleDropOp(e, op)}
                              onDragEnd={handleDragEndOp}
                            >
                              {/* Nó na timeline */}
                              <div className={`absolute -left-[2.55rem] w-5 h-5 rounded-full border-2 border-white shadow ${
                                catIdx === 0 ? 'bg-blue-400' :
                                catIdx === 1 ? 'bg-amber-400' :
                                catIdx === 2 ? 'bg-green-400' :
                                catIdx === 3 ? 'bg-purple-400' :
                                'bg-stone-400'
                              }`}>
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-bold">
                                  {opIdx + 1}
                                </span>
                              </div>

                              {/* Card da operação */}
                              <Card className="p-3 hover:shadow-md transition-all">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Handle de arrastar */}
                                    <GripVertical className="h-4 w-4 text-stone-300 flex-shrink-0" />
                                    {/* Código da operação - MAIS VISÍVEL */}
                                    <div className="px-3 py-1.5 bg-primary/10 border-2 border-primary/30 rounded-md">
                                      <span className="text-sm font-bold font-mono text-primary">{op.codigo}</span>
                                    </div>
                                    <span className="font-medium text-stone-800">{op.nome}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1 text-stone-500">
                                      <Clock className="h-4 w-4" />
                                      <span className="text-sm font-medium">{formatTempo(op.tempo_standard)}</span>
                                    </div>
                                    {/* Indicador de cores de eficiência */}
                                    <div className="flex gap-1">
                                      <div className="w-2 h-2 rounded-full bg-red-500" title={`0-${op.eficiencia_vermelho_max || 75}%`}></div>
                                      <div className="w-2 h-2 rounded-full bg-yellow-500" title={`${op.eficiencia_amarelo_min || 76}-${op.eficiencia_amarelo_max || 89}%`}></div>
                                      <div className="w-2 h-2 rounded-full bg-green-500" title={`${op.eficiencia_verde_min || 90}-${op.eficiencia_verde_max || 100}%`}></div>
                                      <div className="w-2 h-2 rounded-full bg-blue-500" title={`>${op.eficiencia_azul_min || 101}%`}></div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7" 
                                        onClick={() => handleInsertOperacao(cat.id, op, 'above')}
                                        title="Inserir operação acima"
                                      >
                                        <ArrowUp className="h-3 w-3" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7" 
                                        onClick={() => handleInsertOperacao(cat.id, op, 'below')}
                                        title="Inserir operação abaixo"
                                      >
                                        <ArrowDown className="h-3 w-3" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(op)}>
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          ))}
                        </div>

                        {/* Seta para próxima categoria */}
                        {catIdx < categorias.length - 1 && (
                          <div className="flex items-center justify-center my-2">
                            <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                              <ChevronRight className="h-5 w-5 text-stone-400 rotate-90" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legenda de cores de eficiência */}
                <Card className="p-4 mt-6">
                  <h4 className="font-medium text-stone-800 mb-3">Legenda de Eficiência</h4>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span className="text-sm text-stone-600">Vermelho: 0-75%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                      <span className="text-sm text-stone-600">Amarelo: 76-89%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-green-500"></div>
                      <span className="text-sm text-stone-600">Verde: 90-100%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-stone-600">Azul: &gt;101%</span>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Tab Minutas */}
        {activeTab === 'modelos' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modelos.length === 0 ? (
              <Card className="col-span-full p-12 text-center">
                <Layers className="h-16 w-16 mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500 mb-4">Nenhuma minuta criada</p>
                <p className="text-sm text-stone-400">
                  As minutas permitem reutilizar conjuntos de operações ao criar Ordens de Fabrico
                </p>
              </Card>
            ) : (
              modelos.map((modelo) => {
                // Calcular tempo total do modelo
                const tempoTotal = (modelo.operacoes || []).reduce((acc, op) => {
                  const opInfo = operacoes.find(o => o.id === op.operacao_id);
                  return acc + (op.tempo_ajustado || opInfo?.tempo_standard || 0);
                }, 0);
                
                return (
                  <Card key={modelo.id} className="p-5 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                          <Layers className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-stone-900">{modelo.nome}</h3>
                          <div className="flex items-center gap-2 text-xs text-stone-500">
                            <span>{modelo.operacoes?.length || 0} operações</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTempo(tempoTotal)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditModelo(modelo)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-error" onClick={() => handleDeleteModelo(modelo)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {modelo.descricao && (
                      <p className="text-sm text-stone-600 mb-3">{modelo.descricao}</p>
                    )}
                    
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {(modelo.operacoes || []).map((op, idx) => {
                        const opInfo = operacoes.find(o => o.id === op.operacao_id);
                        const tempo = op.tempo_ajustado || opInfo?.tempo_standard || 0;
                        const opsAtribuidos = op.operadores_atribuidos || [];
                        
                        return (
                          <div key={op.operacao_id || `op-${idx}`} className="flex items-center gap-2 text-sm p-2 bg-stone-50 rounded border border-stone-100">
                            <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-bold">
                              {opInfo?.codigo || op.codigo}
                            </span>
                            <span className="font-medium text-stone-700 flex-1 truncate">
                              {opInfo?.nome || op.nome || 'Operação'}
                            </span>
                            <span className="text-xs text-stone-400">{tempo}s</span>
                            {op.grupo && (
                              <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] rounded font-medium">
                                {op.grupo}
                              </span>
                            )}
                            {opsAtribuidos.length > 0 && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded">
                                {opsAtribuidos.length} ops
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Tab Categorias */}
        {activeTab === 'categorias' && (
          <div className="space-y-6">
            {/* Adicionar nova categoria */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-stone-900">Categorias de Operações</h2>
                <Button onClick={() => { resetCategoriaForm(); setShowCategoriaDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Categoria
                </Button>
              </div>
              <p className="text-sm text-stone-500 mb-4">
                As categorias ajudam a organizar as operações por área de produção. <strong>Arraste para reordenar.</strong>
              </p>

              {categorias.length === 0 ? (
                <div className="text-center py-12">
                  <Palette className="h-16 w-16 mx-auto text-stone-300 mb-4" />
                  <p className="text-stone-500 mb-2">Nenhuma categoria criada</p>
                  <p className="text-sm text-stone-400">Crie categorias para organizar as operações</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {categorias
                    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                    .map((categoria, idx) => {
                    const operacoesCategoria = categoria.operacoes_ids?.length || 0;
                    return (
                      <div 
                        key={categoria.id}
                        draggable
                        onDragStart={(e) => handleDragStartCategoria(e, categoria)}
                        onDragOver={(e) => handleDragOverCategoria(e, categoria)}
                        onDrop={(e) => handleDropCategoria(e, categoria)}
                        onDragEnd={handleDragEndCategoria}
                        className={`flex items-center justify-between p-4 bg-white rounded-lg border-2 cursor-move transition-all
                          ${draggedCategoria?.id === categoria.id ? 'opacity-50 scale-95 border-dashed' : 'border-stone-200'}
                          ${dragOverCategoria?.id === categoria.id ? 'border-primary ring-2 ring-primary/20' : ''}
                          hover:shadow-md hover:border-stone-300
                        `}
                      >
                        <div className="flex items-center gap-4">
                          {/* Número de ordem */}
                          <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-sm font-bold text-stone-500">
                            {idx + 1}
                          </div>
                          
                          {/* Ícone de arrastar */}
                          <div className="text-stone-400 cursor-grab active:cursor-grabbing">
                            <GripVertical className="h-5 w-5" />
                          </div>
                          
                          {/* Cor e info */}
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: categoria.cor || '#3b82f6' }}
                          >
                            <Palette className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-stone-900">{categoria.nome}</span>
                              {categoria.codigo && (
                                <span className="text-xs font-mono px-1.5 py-0.5 bg-navy/10 text-navy rounded">
                                  {categoria.codigo}
                                </span>
                              )}
                              {categoria.especial && (
                                <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
                                  Especial
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500">
                              {operacoesCategoria} operação(ões) configurada{operacoesCategoria !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditCategoria(categoria)}
                            className="h-8 w-8"
                          >
                            <Edit2 className="h-4 w-4 text-stone-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCategoria(categoria.id)}
                            className="h-8 w-8"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Dialog Criar/Editar Categoria */}
      <Dialog open={showCategoriaDialog} onOpenChange={(open) => { setShowCategoriaDialog(open); if (!open) resetCategoriaForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitCategoria} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nome *</label>
                <Input
                  value={categoriaForm.nome}
                  onChange={(e) => setCategoriaForm({...categoriaForm, nome: e.target.value})}
                  placeholder="ex: Costura, Acabamentos..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Código * <span className="text-xs text-stone-400 font-normal">(ex: INT, ACA)</span>
                </label>
                <Input
                  value={categoriaForm.codigo || ''}
                  onChange={(e) => setCategoriaForm({...categoriaForm, codigo: e.target.value.toUpperCase()})}
                  placeholder="ex: PROD"
                  maxLength={8}
                  className="font-mono uppercase w-28"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Cor</label>
              <div className="flex flex-wrap gap-2">
                {CORES_CATEGORIAS.map((cor) => (
                  <button
                    key={cor}
                    type="button"
                    onClick={() => setCategoriaForm({...categoriaForm, cor})}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${categoriaForm.cor === cor ? 'ring-2 ring-offset-2 ring-primary' : ''}`}
                    style={{ backgroundColor: cor }}
                  />
                ))}
              </div>
            </div>

            {/* Checkbox Destaque APP */}
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <input
                type="checkbox"
                id="destaque_app"
                checked={categoriaForm.destaque_app}
                onChange={(e) => setCategoriaForm({...categoriaForm, destaque_app: e.target.checked})}
                className="h-5 w-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="destaque_app" className="flex-1 cursor-pointer">
                <span className="font-medium text-amber-800">Destaque APP</span>
                <p className="text-xs text-amber-600">Cria um botão de atalho na app do operador com badge de lotes novos</p>
              </label>
            </div>

            {/* Checkbox Categoria Especial */}
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <input
                type="checkbox"
                id="especial"
                checked={categoriaForm.especial}
                onChange={(e) => setCategoriaForm({...categoriaForm, especial: e.target.checked})}
                className="h-5 w-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="especial" className="flex-1 cursor-pointer">
                <span className="font-medium text-purple-800">Categoria Especial</span>
                <p className="text-xs text-purple-600">Categoria que pode conter operações de várias categorias normais (ex: PROTO, AMOSTRA)</p>
              </label>
            </div>

            {/* Seleção de Operações */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Operações da Categoria
                <span className="text-xs text-stone-400 font-normal ml-2">
                  ({categoriaForm.operacoes_ids.length} selecionada{categoriaForm.operacoes_ids.length !== 1 ? 's' : ''})
                </span>
              </label>
              
              {/* Pesquisa */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Pesquisar operações..."
                  value={searchOperacao}
                  onChange={(e) => setSearchOperacao(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Operações Selecionadas (com ordem) */}
              {categoriaForm.operacoes_ids.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-stone-500 mb-2 font-medium">Selecionadas (arraste para reordenar):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto bg-green-50 p-2 rounded-lg border border-green-200">
                    {categoriaForm.operacoes_ids.map((opId, idx) => {
                      const op = operacoes.find(o => o.id === opId);
                      if (!op) return null;
                      return (
                        <div
                          key={opId}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', idx.toString());
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                            const toIdx = idx;
                            if (fromIdx !== toIdx) {
                              const newIds = [...categoriaForm.operacoes_ids];
                              const [moved] = newIds.splice(fromIdx, 1);
                              newIds.splice(toIdx, 0, moved);
                              setCategoriaForm({...categoriaForm, operacoes_ids: newIds});
                            }
                          }}
                          className="flex items-center gap-2 p-2 bg-white rounded border cursor-move hover:shadow-sm"
                        >
                          <GripVertical className="h-4 w-4 text-stone-400 flex-shrink-0" />
                          <span className="text-xs font-mono text-stone-500 w-12">{idx + 1}.</span>
                          <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">{op.codigo}</span>
                          <span className="text-sm flex-1 truncate">{op.nome}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 flex-shrink-0"
                            onClick={() => {
                              setCategoriaForm({
                                ...categoriaForm,
                                operacoes_ids: categoriaForm.operacoes_ids.filter(id => id !== opId)
                              });
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Lista de Operações Disponíveis - Agrupadas por Categoria */}
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {categorias
                  .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                  .map((cat) => {
                    // Filtrar operações desta categoria
                    const opsCategoria = operacoes
                      .filter(op => op.categoria === cat.nome)
                      .filter(op => {
                        const search = searchOperacao.toLowerCase();
                        return (
                          op.codigo?.toLowerCase().includes(search) ||
                          op.nome?.toLowerCase().includes(search)
                        );
                      })
                      .sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));
                    
                    if (opsCategoria.length === 0) return null;
                    
                    return (
                      <div key={cat.id}>
                        {/* Header da Categoria */}
                        <div 
                          className="sticky top-0 px-3 py-1.5 text-xs font-semibold text-white flex items-center gap-2"
                          style={{ backgroundColor: cat.cor || '#6b7280' }}
                        >
                          <span>{cat.nome}</span>
                          <span className="opacity-70">({opsCategoria.length})</span>
                        </div>
                        
                        {/* Operações da Categoria */}
                        {opsCategoria.map((op) => {
                          const isSelected = categoriaForm.operacoes_ids.includes(op.id);
                          return (
                            <label
                              key={op.id}
                              className={`flex items-center gap-3 p-2 border-b last:border-b-0 cursor-pointer hover:bg-stone-50 transition-colors ${isSelected ? 'bg-green-50' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCategoriaForm({
                                      ...categoriaForm,
                                      operacoes_ids: [...categoriaForm.operacoes_ids, op.id]
                                    });
                                  } else {
                                    setCategoriaForm({
                                      ...categoriaForm,
                                      operacoes_ids: categoriaForm.operacoes_ids.filter(id => id !== op.id)
                                    });
                                  }
                                }}
                                className="h-4 w-4 rounded border-stone-300 text-primary focus:ring-primary"
                              />
                              <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded w-16 text-center">{op.codigo}</span>
                              <span className="text-sm flex-1">{op.nome}</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCategoriaDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" disabled={loading || !categoriaForm.codigo}>
                {loading ? 'A guardar...' : (editingCategoria ? 'Guardar' : 'Criar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar/Editar Operação */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingOperacao ? 'Editar Operação' : 'Nova Operação'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Código *</label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})}
                  placeholder="ex: AC001"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Tempo Standard (seg) *</label>
                <Input
                  type="number"
                  value={formData.tempo_standard}
                  onChange={(e) => setFormData({...formData, tempo_standard: e.target.value})}
                  placeholder="ex: 45"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Nome *</label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="ex: Inspeção de Qualidade"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Categoria *</label>
              <div className="flex gap-2 flex-wrap">
                {categorias.map((cat) => {
                  const isSelected = formData.categoria === cat.nome && !formData.apenas_tarefas;
                  return (
                    <Button
                      key={cat.id}
                      type="button"
                      variant="outline"
                      onClick={() => setFormData({...formData, categoria: cat.nome, apenas_tarefas: false})}
                      className={`rounded-full capitalize transition-all ${
                        isSelected 
                          ? 'bg-primary text-white border-primary hover:bg-primary/90 ring-2 ring-primary ring-offset-2' 
                          : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                      }`}
                    >
                      {cat.nome}
                    </Button>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({...formData, categoria: '', apenas_tarefas: true})}
                  className={`rounded-full transition-all ${
                    formData.apenas_tarefas 
                      ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700 ring-2 ring-purple-400 ring-offset-2' 
                      : 'bg-purple-50 text-purple-600 border-purple-200 hover:border-purple-400 hover:bg-purple-100'
                  }`}
                >
                  Apenas Tarefas
                </Button>
              </div>
              {formData.apenas_tarefas && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Esta operação <strong>só aparecerá nas Tarefas Pontuais</strong>, não nas OFs normais.</span>
                  </p>
                </div>
              )}
            </div>

            {/* Operação de Contagem */}
            <div className="bg-sky-50 border border-sky-200 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block font-medium text-stone-800">Operação de Contagem</span>
                    <span className="text-xs text-stone-500">Ajusta automaticamente as quantidades das operações seguintes</span>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_contagem || false}
                    onChange={(e) => setFormData({...formData, is_contagem: e.target.checked, tipo_contagem: formData.tipo_contagem || 'fim'})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                </label>
              </div>

              {/* Tipo de contagem — visível para TODAS as operações */}
              <div className="mt-4 pt-4 border-t border-stone-200 space-y-2">
                <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-3">Modo de Registo de Peças</p>
                <p className="text-xs text-stone-500 mb-3">Define como o operador regista as peças produzidas nesta operação</p>
                
                {/* Opção: Contagem Unitária */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    (formData.tipo_contagem || 'fim') === 'unitaria'
                      ? 'border-primary bg-primary/5'
                      : 'border-stone-200 bg-white hover:border-primary/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo_contagem"
                    value="unitaria"
                    checked={(formData.tipo_contagem || 'fim') === 'unitaria'}
                    onChange={() => setFormData({...formData, tipo_contagem: 'unitaria'})}
                    className="mt-0.5 h-4 w-4 text-primary"
                    data-testid="tipo-contagem-unitaria"
                  />
                  <div>
                    <span className="block font-semibold text-stone-800 text-sm">Contagem Unitária</span>
                    <span className="block text-xs text-stone-500 mt-0.5">
                      O operador conta peça a peça com botões +1/+2/+5/+10. Vai acumulando até ao limite do lote.
                    </span>
                  </div>
                </label>

                {/* Opção: Contagem só no Fim */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.tipo_contagem === 'fim'
                      ? 'border-primary bg-primary/5'
                      : 'border-stone-200 bg-white hover:border-primary/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo_contagem"
                    value="fim"
                    checked={formData.tipo_contagem === 'fim'}
                    onChange={() => setFormData({...formData, tipo_contagem: 'fim'})}
                    className="mt-0.5 h-4 w-4 text-primary"
                    data-testid="tipo-contagem-fim"
                  />
                  <div>
                    <span className="block font-semibold text-stone-800 text-sm">Fechar no Fim</span>
                    <span className="block text-xs text-stone-500 mt-0.5">
                      O operador insere o total produzido apenas ao fechar a operação. Mais simples, adequado para contagens rápidas.
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Código de Cores */}
            <div className="bg-stone-50 p-4 rounded-lg">
              <h4 className="text-sm font-semibold text-stone-700 mb-3">Faixas de Eficiência (%)</h4>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="w-4 h-4 rounded-full bg-red-500 mx-auto mb-1"></div>
                  <Input
                    type="number"
                    value={formData.eficiencia_vermelho_max}
                    onChange={(e) => setFormData({...formData, eficiencia_vermelho_max: parseInt(e.target.value)})}
                    className="text-center text-sm h-8"
                  />
                  <span className="text-xs text-stone-400">0-max</span>
                </div>
                <div className="text-center">
                  <div className="w-4 h-4 rounded-full bg-yellow-500 mx-auto mb-1"></div>
                  <Input
                    type="number"
                    value={formData.eficiencia_amarelo_max}
                    onChange={(e) => setFormData({...formData, eficiencia_amarelo_max: parseInt(e.target.value)})}
                    className="text-center text-sm h-8"
                  />
                  <span className="text-xs text-stone-400">max</span>
                </div>
                <div className="text-center">
                  <div className="w-4 h-4 rounded-full bg-green-500 mx-auto mb-1"></div>
                  <Input
                    type="number"
                    value={formData.eficiencia_verde_max}
                    onChange={(e) => setFormData({...formData, eficiencia_verde_max: parseInt(e.target.value)})}
                    className="text-center text-sm h-8"
                  />
                  <span className="text-xs text-stone-400">max</span>
                </div>
                <div className="text-center">
                  <div className="w-4 h-4 rounded-full bg-blue-500 mx-auto mb-1"></div>
                  <Input
                    type="number"
                    value={formData.eficiencia_azul_min}
                    onChange={(e) => setFormData({...formData, eficiencia_azul_min: parseInt(e.target.value)})}
                    className="text-center text-sm h-8"
                  />
                  <span className="text-xs text-stone-400">min+</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowDialog(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 rounded-full" disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'A guardar...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar/Editar Minuta - FULLSCREEN */}
      <Dialog open={showModeloDialog} onOpenChange={(open) => { setShowModeloDialog(open); if (!open) resetModeloForm(); }}>
        <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingModelo ? 'Editar Minuta' : 'Nova Minuta de Operações'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitModelo} className="flex-1 flex flex-col overflow-hidden space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">Nome da Minuta *</label>
                <Input
                  value={modeloForm.nome}
                  onChange={(e) => setModeloForm({...modeloForm, nome: e.target.value})}
                  placeholder="ex: Acabamento Calça Jeans"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">Descrição</label>
                <Input
                  value={modeloForm.descricao}
                  onChange={(e) => setModeloForm({...modeloForm, descricao: e.target.value})}
                  placeholder="Descrição opcional"
                />
              </div>
            </div>

            <div className="grid grid-cols-5 gap-6 flex-1 min-h-0 overflow-hidden">
              {/* Operações Disponíveis - Agrupadas por Categoria (2/5 do espaço) */}
              <div className="col-span-2 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-stone-700">
                    Operações Disponíveis ({operacoes.length})
                  </label>
                </div>
                
                {/* Seletor rápido de categorias personalizadas */}
                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 mb-2">Adicionar por Categoria Personalizada:</p>
                  <div className="flex flex-wrap gap-1">
                    {categorias
                      .filter(cat => cat.operacoes_ids && cat.operacoes_ids.length > 0)
                      .map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            // Adicionar todas as operações desta categoria personalizada
                            const opsToAdd = cat.operacoes_ids
                              .map(opId => operacoes.find(o => o.id === opId))
                              .filter(Boolean)
                              .filter(op => !modeloForm.operacoes.some(o => (o.operacao_id || o.id) === op.id));
                            
                            if (opsToAdd.length > 0) {
                              const newOps = opsToAdd.map(op => ({
                                operacao_id: op.id,
                                codigo: op.codigo,
                                nome: op.nome,
                                tempo_ajustado: op.tempo_standard,
                                tempo_standard: op.tempo_standard,
                                categoria: op.categoria,
                                ordem: op.ordem || 0,
                                grupo: null,
                                operadores_atribuidos: []
                              }));
                              
                              const todasOperacoes = [...modeloForm.operacoes, ...newOps];
                              
                              // Ordenar pela ordem da categoria e ordem da operação
                              todasOperacoes.sort((a, b) => {
                                const catA = categorias.find(c => c.nome === a.categoria);
                                const catB = categorias.find(c => c.nome === b.categoria);
                                const ordemCatA = catA?.ordem ?? 999;
                                const ordemCatB = catB?.ordem ?? 999;
                                
                                if (ordemCatA !== ordemCatB) {
                                  return ordemCatA - ordemCatB;
                                }
                                return (a.ordem || 0) - (b.ordem || 0);
                              });
                              
                              setModeloForm({
                                ...modeloForm,
                                operacoes: todasOperacoes
                              });
                              toast.success(`${opsToAdd.length} operações adicionadas da categoria "${cat.nome}"`);
                            } else {
                              toast.info('Todas as operações desta categoria já estão no modelo');
                            }
                          }}
                          className="px-2 py-1 text-xs rounded-full border hover:bg-amber-100 transition-colors flex items-center gap-1"
                          style={{ borderColor: cat.cor, color: cat.cor }}
                        >
                          <Plus className="h-3 w-3" />
                          {cat.nome} ({cat.operacoes_ids.length})
                        </button>
                      ))}
                    {categorias.filter(cat => cat.operacoes_ids && cat.operacoes_ids.length > 0).length === 0 && (
                      <p className="text-xs text-amber-600 italic">Nenhuma categoria tem operações configuradas</p>
                    )}
                  </div>
                </div>
                
                <div className="border rounded-lg p-2 flex-1 overflow-y-auto space-y-2">
                  {categorias.map((cat) => {
                    const operacoesDaCategoria = operacoes.filter(op => op.categoria === cat.nome);
                    if (operacoesDaCategoria.length === 0) return null;
                    
                    const isExpanded = expandedModeloCategories.includes(cat.id);
                    const numSelected = operacoesDaCategoria.filter(op => 
                      modeloForm.operacoes.some(o => (o.operacao_id || o.id) === op.id)
                    ).length;
                    
                    return (
                      <div key={cat.id} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedModeloCategories(prev => 
                            prev.includes(cat.id) 
                              ? prev.filter(id => id !== cat.id)
                              : [...prev, cat.id]
                          )}
                          className="w-full flex items-center justify-between p-2 bg-stone-50 hover:bg-stone-100 text-sm font-medium text-stone-700"
                        >
                          <div className="flex items-center gap-2">
                            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                            <span className="capitalize">{cat.nome}</span>
                            <span className="text-xs text-stone-400">({operacoesDaCategoria.length})</span>
                          </div>
                          {numSelected > 0 && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                              {numSelected} sel.
                            </span>
                          )}
                        </button>
                        {isExpanded && (
                          <div className="p-1 space-y-1 max-h-48 overflow-y-auto">
                            {operacoesDaCategoria.map((op) => {
                              const isSelected = modeloForm.operacoes.some(o => (o.operacao_id || o.id) === op.id);
                              return (
                                <button
                                  key={op.id}
                                  type="button"
                                  onClick={() => toggleOperacaoNoModelo(op)}
                                  className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm transition-colors ${
                                    isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-stone-50'
                                  }`}
                                >
                                  <span className="font-mono text-xs text-stone-400">{op.codigo}</span>
                                  <span className="flex-1 truncate">{op.nome}</span>
                                  <span className="text-xs text-stone-500">{formatTempo(op.tempo_standard)}</span>
                                  {isSelected && <ChevronRight className="h-4 w-4" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Operações Selecionadas (3/5 do espaço - MAIOR) */}
              <div className="col-span-3 flex flex-col min-h-0">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Operações no Modelo ({modeloForm.operacoes.length})
                </label>
                <div className="border rounded-lg p-3 flex-1 overflow-y-auto space-y-3">
                  {modeloForm.operacoes.length === 0 ? (
                    <p className="text-center text-stone-400 py-8 text-sm">
                      Selecione operações à esquerda
                    </p>
                  ) : (
                    <>
                      {modeloForm.operacoes.map((op, idx) => {
                        const opInfo = operacoes.find(o => o.id === (op.operacao_id || op.id));
                        const operadoresAttr = op.operadores_atribuidos || [];
                        const tempo = op.tempo_ajustado || opInfo?.tempo_standard || 0;
                        
                        // Encontrar categoria da operação para filtrar operadores
                        const categoriaOp = categorias.find(c => c.nome === opInfo?.categoria);
                        const operadoresFiltrados = filtrarOperadoresPorPermissao(op.operacao_id || op.id, categoriaOp?.id);
                        
                        return (
                          <div 
                            key={op.operacao_id || op.id || `modelo-op-${idx}`} 
                            className={`p-3 bg-white rounded-lg border transition-all cursor-move ${
                              op.grupo ? 'border-l-4 border-l-primary' : 'border-stone-100'
                            } ${draggedModeloOp === idx ? 'opacity-50 scale-95' : ''} ${
                              dragOverModeloOp === idx ? 'ring-2 ring-primary ring-offset-2' : ''
                            }`}
                            draggable
                            onDragStart={(e) => handleDragStartModeloOp(e, idx)}
                            onDragOver={(e) => handleDragOverModeloOp(e, idx)}
                            onDrop={(e) => handleDropModeloOp(e, idx)}
                            onDragEnd={handleDragEndModeloOp}
                          >
                            {/* Linha 1: ID + Nome + Tempo */}
                            <div className="flex items-center gap-2 mb-2">
                              <GripVertical className="h-4 w-4 text-stone-300" />
                              <span className="text-xs font-bold text-white bg-primary rounded-full w-5 h-5 flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <span className="font-mono text-xs font-bold px-2 py-0.5 bg-primary/10 text-primary rounded border border-primary/20">
                                {opInfo?.codigo || op.codigo}
                              </span>
                              <span className="text-sm font-medium flex-1 truncate">
                                {opInfo?.nome || op.nome}
                              </span>
                              {opInfo?.categoria && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${getCategoriaColor(opInfo.categoria)}`}>
                                  {opInfo.categoria}
                                </span>
                              )}
                              <div className="flex items-center gap-1 bg-stone-50 px-2 py-1 rounded">
                                <Clock className="h-3 w-3 text-stone-400" />
                                <input
                                  type="number"
                                  value={tempo}
                                  onChange={(e) => {
                                    const newOps = [...modeloForm.operacoes];
                                    newOps[idx] = { ...newOps[idx], tempo_ajustado: parseInt(e.target.value) || 0 };
                                    setModeloForm({ ...modeloForm, operacoes: newOps });
                                  }}
                                  className="w-12 text-xs border-0 bg-transparent text-center font-medium"
                                />
                                <span className="text-xs text-stone-400">s</span>
                              </div>
                              <select
                                value={op.grupo || ''}
                                onChange={(e) => updateOperacaoGrupo(op.operacao_id || op.id, e.target.value || null)}
                                className="text-xs border rounded px-2 py-1"
                              >
                                <option value="">-</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => toggleOperacaoNoModelo({ id: op.operacao_id || op.id })}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            
                            {/* Linha 2: Operadores Atribuídos */}
                            <div className="pl-7">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-xs text-stone-500 flex items-center gap-1 mr-1">
                                  <Users className="h-3 w-3" />
                                  Operadores:
                                </span>
                                {operadoresFiltrados.slice(0, 12).map(opr => (
                                  <button
                                    key={opr.id}
                                    type="button"
                                    onClick={() => toggleOperadorNoModelo(idx, opr.id)}
                                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                      operadoresAttr.includes(opr.id)
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-white text-stone-600 border-stone-200 hover:border-primary hover:text-primary'
                                    }`}
                                  >
                                    {formatarNomeOperador(opr.nome_completo) || opr.username}
                                  </button>
                                ))}
                                {operadoresFiltrados.length === 0 && (
                                  <span className="text-[10px] text-stone-400 italic">Sem operadores com permissão</span>
                                )}
                                {operadoresAttr.length > 0 && (
                                  <span className="text-[10px] text-green-600 ml-1">
                                    ({operadoresAttr.length} selecionado{operadoresAttr.length > 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
                  <p className="text-xs text-blue-700">
                    <strong>Dica:</strong> Os operadores associados serão pré-selecionados quando carregar este modelo numa nova OF.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 flex-shrink-0 border-t mt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowModeloDialog(false); resetModeloForm(); }}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 rounded-full" disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'A guardar...' : 'Guardar Modelo'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
